// EL HISTORIAL DE SUBIDAS TIENE QUE EXPLICAR TODO EL DINERO.
//
// Maricarmen Nieto (Pomares), 2026-09-10, por soporte: sus comisiones no
// cuadraban. Borraba TODO lo que veia en "Historial de Subidas", el historial
// quedaba vacio, y el monto seguia sin cuadrar.
//
// No era cosa suya. La pantalla solo agrupaba las filas marcadas como subida, y
// habia comisiones —las del importador de CSV y las viejas, de antes de que
// existiera esa marca— que SUMAN en el total y no salian por ningun lado. Un
// historial que enseña una parte del dinero no sirve para cuadrar: sirve para
// que quien lo mira crea que ya no queda nada.
//
// Esto vive suelto, sin React, para que la prueba del servidor pueda comprobar
// que las dos mitades —lo que viene de una subida y lo que no— suman SIEMPRE el
// total, sin importar como esten marcadas las filas.

/**
 * Una comision viene de un archivo si lo dice CUALQUIERA de sus rastros: su
 * marca de origen, el nombre del archivo, la marca del lote, o el archivo al
 * que esta atada en la base. El de la base es el que recupera las que ya
 * estaban guardadas sin sello (cambiar como se escribe no cambia lo ya escrito).
 *
 * `source` VA EL PRIMERO Y NO SOBRA, aunque parezca redundante: es con lo que
 * el historial agrupa los lotes. La primera version de esto lo dejo fuera —dos
 * definiciones de la misma cosa— y el resultado se vio al abrir la pantalla:
 * las MISMAS 151 comisiones salian en las dos mitades y el "total" era el doble
 * del real ($18.922 sobre $9.461). Un cuadre que cuenta dos veces es peor que
 * no tener cuadre. Las dos mitades tienen que ser complementarias por
 * construccion, y por eso el historial agrupa con esta misma funcion.
 */
export const vieneDeUnaSubida = (c) =>
  !!(c && (c.source === 'upload' || c.srcFile || c.batchTs || c.commissionFileId));

/**
 * LAS FILAS que no encajan en ninguna subida.
 *
 * Se devuelven ENTERAS, no solo contadas, porque la pantalla tiene que poder
 * enseñarlas una por una antes de que nadie borre nada. Ese era el hueco de la
 * primera version: se decia cuanto dinero habia fuera y no se dejaba tocarlo,
 * asi que la agente veia el descuadre y no podia hacer nada con el (Lissette,
 * 2026-09-11: *"que si la persona borra del historial algo que subio, esa se
 * borre de por si y no cuente mas, no acumule; eso es un dolor de cabeza"*).
 */
export function filasFueraDeSubidas(comisiones) {
  return (Array.isArray(comisiones) ? comisiones : []).filter((c) => !vieneDeUnaSubida(c));
}

/**
 * Lo que NO encaja en ninguna subida: cuantas filas y cuanto suman.
 *
 * Sale de la MISMA lista que se enseña y se borra, para que el numero del aviso
 * y el de la confirmacion no puedan decir cosas distintas.
 */
export function loQueNoVieneDeUnaSubida(comisiones) {
  const fuera = filasFueraDeSubidas(comisiones);
  let total = 0;
  for (const c of fuera) total += Number(c && c.amount) || 0;
  return { filas: fuera.length, total: Math.round(total * 100) / 100 };
}

/**
 * El cuadre completo, que es lo que se enseña abajo del historial: lo de las
 * subidas, lo de fuera, y el total de las dos cosas. Se calcula aqui —y no
 * sumando lo que pinta la pantalla— para que el numero grande sea el de TODAS
 * las comisiones y no el de las que se lograron agrupar.
 */
export function cuadreDeComisiones(comisiones, subidas) {
  const lista = Array.isArray(comisiones) ? comisiones : [];
  const lotes = Array.isArray(subidas) ? subidas : [];
  const deSubidas = {
    filas: lotes.reduce((s, b) => s + (b.count || 0), 0),
    total: Math.round(lotes.reduce((s, b) => s + (Number(b.total) || 0), 0) * 100) / 100,
  };
  const fuera = loQueNoVieneDeUnaSubida(lista);
  return {
    deSubidas,
    fuera,
    total: Math.round((deSubidas.total + fuera.total) * 100) / 100,
    filas: deSubidas.filas + fuera.filas,
  };
}
